import {
  Component,
  contentChildren,
  effect,
  input,
  model,
  output,
  Signal,
  signal
} from '@angular/core';
import { EMPTY, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { outputToObservable, toObservable } from '@angular/core/rxjs-interop';

import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { CardComponent } from './card.component';

import type { AfterContentInit, InputSignal, ModelSignal, OnDestroy, OutputEmitterRef } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import type { ChangeEventOn, SelectionChangeEvent } from '../../domain/selectable.domain';

/**
 * Dual-support control shape: `value` is the single source of truth and doubles as the
 * Angular Signal Forms native custom-control binding. The `ControlValueAccessor` below is a
 * thin adapter required only by `[(ngModel)]` / `[formControl]`.
 *
 * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 */
@Component({
  selector: 'td-wb-card-group',
  template: '<ng-content />',
  providers: [ValueAccessorProviderFactory(CardGroupComponent)]
})
export class CardGroupComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  readonly cards: Signal<ReadonlyArray<CardComponent>> = contentChildren(CardComponent, { descendants: true });
  private readonly cards$ = toObservable(this.cards);

  singleSelection: InputSignal<boolean> = input(true);
  maxSelection: InputSignal<number | undefined> = input<number | undefined>(undefined);

  /** Single source of truth, and the Signal Forms native custom-control binding. */
  readonly value: ModelSignal<unknown | Array<unknown>> = model<unknown | Array<unknown>>(undefined);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly disabledByOwner = signal(false);

  private readonly optionSelectionChanges: ChangeEventOn<CardComponent> =
    this.cards$.pipe(
      switchMap((cards: ReadonlyArray<CardComponent>): ChangeEventOn<CardComponent> => {
        if (cards.length === 0) {
          return EMPTY;
        }
        return merge(...cards.map((item: CardComponent): ChangeEventOn<CardComponent> => outputToObservable(item.action)));
      })
    );

  constructor() {
    this.cards$.pipe(takeUntil(this.destroy)).subscribe((cards: ReadonlyArray<CardComponent>): void => {
      cards.forEach((card: CardComponent): void => card.selectable.set(true));
    });

    // Imperative push of derived state into projected child components. This is the
    // sanctioned use of effect(): syncing signal state to a non-signal (imperative) API.
    effect((): void => {
      this.updateSelectedCardFromValue();
    });
  }

  ngAfterContentInit(): void {
    this.optionSelectionChanges.pipe(takeUntil(this.destroy))
      .subscribe((event: SelectionChangeEvent<CardComponent>): void => {
        this.resetSelections(event);
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  // ControlValueAccessor: owner -> control. Must never emit onChangeCallback.
  writeValue(value: unknown): void {
    this.setValue(value);
  }

  registerOnChange(onChangeCallback: (value: unknown) => void): void {
    this.onChangeCallback = onChangeCallback;
  }

  registerOnTouched(onChangeCallback: () => void): void {
    this.onTouchCallback = onChangeCallback;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  private onChangeCallback: (_: unknown) => void =  (): void => {};
  private onTouchCallback: () => void =  (): void => {};

  /** Normalizes multi-selection writes before storing them, then syncs children synchronously. */
  private setValue(newValue: unknown | Array<unknown>): void {
    if (this.singleSelection()) {
      this.value.set(newValue);
    } else {
      this.value.set(Array.isArray(newValue) ? newValue : null);
    }

    // Synchronous so callers observe updated children immediately; the constructor effect
    // is only a safety net for writes that arrive through the `[(value)]` model binding.
    this.updateSelectedCardFromValue();
  }

  private updateSelectedCardFromValue(): void {
    const cards = this.cards();
    const currentValue = this.value();
    if (this.singleSelection()) {
      cards.forEach((card: CardComponent): void => {
        card.setSelected(currentValue === card.item());
      });
    } else {
      if (currentValue === null || currentValue === undefined) {
        cards.forEach((card: CardComponent): void => {
          card.setSelected(false);
        });
      } else {
        cards.forEach((card: CardComponent): void => {
          if (Array.isArray(currentValue)) {
            card.setSelected(currentValue.indexOf(card.item()) !== -1);
          }
        });
      }
    }
    this.updateDisabledState();
  }

  /** Owner-driven disabled wins; otherwise cards are disabled once max selection is reached. */
  private updateDisabledState(): void {
    const cards = this.cards();

    if (this.disabledByOwner()) {
      cards.forEach((card: CardComponent): void => card.disabled.set(true));
      return;
    }

    const max = this.maxSelection();
    if (max === undefined || this.singleSelection()) {
      cards.forEach((card: CardComponent): void => card.disabled.set(false));
      return;
    }

    const currentValue = this.value();
    const selectionCount = Array.isArray(currentValue) ? currentValue.length : 0;
    const isAtMax = selectionCount >= max;

    cards.forEach((card: CardComponent): void => {
      card.disabled.set(isAtMax && !card.selected());
    });
  }

  private resetSelections(event: SelectionChangeEvent<CardComponent>): void {
    if (this.singleSelection()) {
      this.setValue(event.source.selected() ? event.source.item() : undefined);
    } else {
      this.resetMultiSelections(event);
    }
    if (event.fromView) {
      this.onTouchCallback();
      this.onChangeCallback(this.value());
      this.touch.emit();
    }
  }

  private resetMultiSelections(event: SelectionChangeEvent<CardComponent>): void {
    const currentValue = this.value();
    if (!Array.isArray(currentValue)) {
      this.setValue([event.source.item()]);
      return;
    }
    if (!event.source.selected()) {
      this.setValue(currentValue.filter((item: unknown): boolean => item !== event.source.item()));
      return;
    }
    const max = this.maxSelection();
    if (max !== undefined && currentValue.length >= max) {
      event.source.setSelected(false);
      return;
    }
    this.setValue([...currentValue, event.source.item()]);
  }
}
